import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { BookOpen, CheckCircle, Award, ExternalLink, Download, AlertCircle, Circle } from 'lucide-react';
import { apiFetch } from '../../services/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  green:  { color: 'bg-emerald-500', ring: 'ring-emerald-500/40', text: 'text-emerald-300', label: 'OK' },
  yellow: { color: 'bg-amber-500',   ring: 'ring-amber-500/40',   text: 'text-amber-300',   label: 'Hinweis' },
  red:    { color: 'bg-rose-500',    ring: 'ring-rose-500/40',    text: 'text-rose-300',    label: 'Handeln' },
};

export const InhaberTeamProgress = ({ teamProgress }) => {
  const navigate = useNavigate();
  const team = teamProgress?.team || [];
  const [zipLoading, setZipLoading] = useState(false);
  const [ampel, setAmpel] = useState(null);
  const [ampelLoading, setAmpelLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setAmpelLoading(true);
      try {
        const res = await apiFetch(`${API}/api/certificates/status-overview`);
        const data = await res.json();
        if (!cancelled && data.success) setAmpel(data);
      } catch (e) {
        console.error('Ampel-Übersicht fehlgeschlagen:', e);
      } finally {
        if (!cancelled) setAmpelLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const downloadZip = async () => {
    setZipLoading(true);
    try {
      const res = await apiFetch(`${API}/api/certificates/company-zip`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'ZIP-Download fehlgeschlagen');
      }
      const blob = await res.blob();
      const filename = (res.headers.get('Content-Disposition') || '')
        .match(/filename="?([^"]+)"?/)?.[1] || 'SmartVolt_Zertifikate.zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('ZIP heruntergeladen', { description: 'BG-fertige Ordnerstruktur pro Mitarbeiter.' });
    } catch (err) {
      toast.error(err.message || 'Download nicht möglich');
    } finally {
      setZipLoading(false);
    }
  };

  const counts = ampel?.counts || { green: 0, yellow: 0, red: 0 };
  const rows = ampel?.rows || [];

  return (
    <div className="space-y-6" data-testid="team-progress-tab">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl font-bold text-white">Kursfortschritt Ihres Teams</h2>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={downloadZip}
            disabled={zipLoading}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-semibold hover:from-emerald-400 hover:to-teal-400"
            data-testid="download-cert-zip-btn"
          >
            {zipLoading ? (
              <><div className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full mr-2" /> ZIP wird erstellt…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Alle Zertifikate als ZIP</>
            )}
          </Button>
          <Button onClick={() => navigate('/kurse')} className="bg-yellow-500 text-black hover:bg-yellow-400">
            <ExternalLink className="w-4 h-4 mr-2" /> Schulungskatalog
          </Button>
        </div>
      </div>

      {/* Ampel-Übersicht */}
      <Card className="bg-gray-900/60 border-gray-800" data-testid="ampel-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Compliance-Ampel (BG-Übersicht)</h3>
            {ampel && (
              <span className="text-xs text-gray-500">
                Stand: {new Date(ampel.generated_at).toLocaleString('de-DE')}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-center" data-testid="ampel-green">
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_10px] shadow-emerald-500/60" />
                <span className="text-xs text-emerald-300 uppercase tracking-wider font-semibold">Grün</span>
              </div>
              <p className="text-3xl font-black text-emerald-300">{counts.green}</p>
              <p className="text-[11px] text-emerald-300/70 mt-0.5">gültig</p>
            </div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-center" data-testid="ampel-yellow">
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full bg-amber-400 shadow-[0_0_10px] shadow-amber-400/60" />
                <span className="text-xs text-amber-300 uppercase tracking-wider font-semibold">Gelb</span>
              </div>
              <p className="text-3xl font-black text-amber-300">{counts.yellow}</p>
              <p className="text-[11px] text-amber-300/70 mt-0.5">bald fällig</p>
            </div>
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-center" data-testid="ampel-red">
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full bg-rose-500 shadow-[0_0_10px] shadow-rose-500/60" />
                <span className="text-xs text-rose-300 uppercase tracking-wider font-semibold">Rot</span>
              </div>
              <p className="text-3xl font-black text-rose-300">{counts.red}</p>
              <p className="text-[11px] text-rose-300/70 mt-0.5">Handeln nötig</p>
            </div>
          </div>

          {ampelLoading ? (
            <p className="text-sm text-gray-500 py-4">Lade Übersicht…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">Noch keine Zuweisungen — sobald Sie Kurse zuweisen, sehen Sie hier den Status.</p>
          ) : (
            <div className="rounded-lg border border-gray-800 overflow-hidden">
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-black/60 text-gray-400 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left p-2 pl-3">Status</th>
                      <th className="text-left p-2">Mitarbeiter</th>
                      <th className="text-left p-2">Kurs</th>
                      <th className="text-left p-2">Info</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {rows.slice(0, 100).map((r, i) => {
                      const meta = STATUS_META[r.status] || STATUS_META.yellow;
                      return (
                        <tr key={i} className="hover:bg-white/[0.02]" data-testid={`ampel-row-${r.status}`}>
                          <td className="p-2 pl-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.text} ring-1 ${meta.ring}`}>
                              <span className={`w-2 h-2 rounded-full ${meta.color}`} />
                              {meta.label}
                            </span>
                          </td>
                          <td className="p-2 text-white">{r.employee_name}</td>
                          <td className="p-2 text-gray-300">{r.course_title}</td>
                          <td className="p-2 text-gray-400 text-xs">{r.reason}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rows.length > 100 && (
                <p className="text-xs text-gray-500 text-center py-2 border-t border-gray-800">
                  Zeige 100 von {rows.length} Einträgen — ZIP enthält alle Zertifikate.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {team.length > 0 ? team.map((member) => (
        <Card key={member.id} className="bg-gray-900/50 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-500 font-bold">
                  {member.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-white font-medium">{member.name}</p>
                  <p className="text-sm text-gray-400">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 border">
                  {member.points} SmartVolt-Points
                </Badge>
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 border">
                  {member.level}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-black/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-white">{member.total_enrolled}</p>
                <p className="text-xs text-gray-400">Eingeschrieben</p>
              </div>
              <div className="bg-black/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-cyan-400">{member.in_progress}</p>
                <p className="text-xs text-gray-400">In Bearbeitung</p>
              </div>
              <div className="bg-black/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-green-400">{member.completed}</p>
                <p className="text-xs text-gray-400">Abgeschlossen</p>
              </div>
            </div>

            {member.progress_details?.length > 0 && (
              <div className="space-y-2">
                {member.progress_details.map((prog) => {
                  const mod = teamProgress.modules?.find((m) => m.id === prog.module_id);
                  const pct = prog.total_lessons > 0
                    ? Math.round((prog.completed_lessons?.length || 0) / prog.total_lessons * 100)
                    : 0;
                  return (
                    <div key={prog.module_id} className="flex items-center gap-3 bg-black/20 p-2 rounded">
                      <div className="flex-1">
                        <p className="text-sm text-white">{mod?.title || prog.module_id}</p>
                        <div className="w-full h-1.5 bg-gray-800 rounded mt-1">
                          <div
                            className={`h-full rounded ${prog.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 w-12 text-right">{pct}%</span>
                      {prog.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-500" />}
                    </div>
                  );
                })}
              </div>
            )}

            {member.certificates?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-800">
                <p className="text-xs text-gray-400 mb-2">Zertifikate:</p>
                <div className="flex flex-wrap gap-2">
                  {member.certificates.map((cert, i) => (
                    <Badge key={i} className="bg-green-500/20 text-green-400 border-green-500/30 border text-xs">
                      <Award className="w-3 h-3 mr-1" /> {cert.moduleTitle || cert.courseTitle || 'Zertifikat'}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )) : (
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Noch keine Kursfortschritte verfügbar.</p>
            <p className="text-sm text-gray-500 mt-1">Sobald Ihre Mitarbeiter Kurse starten, sehen Sie hier den Fortschritt.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
